"use client";

import { useState } from "react";
import Image from "next/image";
import { ChevronDown, Plus, Minus, ShoppingCart, Check } from "lucide-react";
import { useCart, formatDkk } from "@/lib/cart/cart-context";
import type { Product } from "@/lib/content/products";
import { marked } from "marked";

type Props = {
  product: Product;
  locale: "da" | "en";
};

export function ProductAccordion({ product, locale }: Props) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [justAdded, setJustAdded] = useState(false);
  const { addItem } = useCart();

  const onSale = product.sale_price_dkk != null;
  const stockLow = product.stock_count > 0 && product.stock_count <= 3;
  const outOfStock = !product.is_in_stock;

  const handleAdd = () => {
    if (outOfStock) return;
    addItem(
      {
        product_id: product.id,
        slug: product.slug,
        name: product.name,
        price_dkk: product.effective_price_dkk,
        image_url: product.primary_image_url,
        image_alt: product.primary_image_alt,
      },
      qty
    );
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1800);
  };

  const html = product.content
    ? (marked.parse(product.content, { gfm: true, breaks: true, async: false }) as string)
    : "";

  return (
    <article
      className={`bg-white border border-[var(--color-line)] rounded-[12px] overflow-hidden transition-shadow ${
        open ? "shadow-[0_8px_22px_rgba(0,23,37,0.08)]" : ""
      } ${outOfStock ? "opacity-70" : ""}`}
    >
      {/* Header — altid synligt */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left p-4 lg:p-5 flex items-center gap-4 hover:bg-[var(--color-surface-50)] transition-colors"
        aria-expanded={open}
      >
        {product.primary_image_url && (
          <div className="relative w-16 h-16 lg:w-20 lg:h-20 rounded-[8px] overflow-hidden bg-[var(--color-surface-100)] flex-shrink-0">
            <Image
              src={product.primary_image_url}
              alt={product.primary_image_alt ?? product.name}
              fill
              className="object-cover"
              sizes="80px"
              unoptimized
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            {onSale && (
              <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#991b1b] bg-[#fee2e2] px-1.5 py-0.5 rounded-[4px]">
                {locale === "en" ? "Sale" : "Tilbud"}
              </span>
            )}
            {stockLow && !outOfStock && (
              <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#92400e] bg-[#fef3c7] px-1.5 py-0.5 rounded-[4px]">
                {locale === "en" ? `Only ${product.stock_count} left` : `Kun ${product.stock_count} tilbage`}
              </span>
            )}
            {outOfStock && (
              <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[var(--color-muted)] bg-[var(--color-surface-200)] px-1.5 py-0.5 rounded-[4px]">
                {locale === "en" ? "Sold out" : "Udsolgt"}
              </span>
            )}
          </div>
          <h3 className="font-[family-name:var(--font-display-loaded)] text-[16px] lg:text-[18px] font-bold text-[var(--color-navy-700)] mb-1 truncate">
            {product.name}
          </h3>
          {product.short_desc && (
            <p className="text-[13px] text-[var(--color-muted)] line-clamp-1">{product.short_desc}</p>
          )}
        </div>

        <div className="text-right flex-shrink-0">
          <div className="font-[family-name:var(--font-display-loaded)] text-[18px] lg:text-[22px] font-bold text-[var(--color-navy-700)] tabular-nums">
            {formatDkk(product.effective_price_dkk, locale)}
          </div>
          {onSale && (
            <div className="text-[12px] text-[var(--color-muted)] line-through tabular-nums">
              {formatDkk(product.price_dkk, locale)}
            </div>
          )}
        </div>

        <ChevronDown
          size={20}
          strokeWidth={2}
          className={`text-[var(--color-muted)] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Foldout */}
      {open && (
        <div className="border-t border-[var(--color-line-soft)] p-4 lg:p-6 bg-[var(--color-surface-50)]/40">
          {html && (
            <div
              className="prose-blog text-[15px] leading-[1.65] text-[var(--color-ink)] mb-5"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}

          {/* Tilføj til kurv */}
          <div className="flex items-center gap-3 flex-wrap">
            {!outOfStock && (
              <div className="inline-flex items-center bg-white border border-[var(--color-line)] rounded-[10px] overflow-hidden">
                <button
                  type="button"
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  disabled={qty <= 1}
                  className="px-3 py-2.5 text-[var(--color-muted)] hover:text-[var(--color-navy-700)] disabled:opacity-30"
                  aria-label={locale === "en" ? "Decrease" : "Færre"}
                >
                  <Minus size={14} strokeWidth={2.5} />
                </button>
                <span className="px-3 font-bold text-[14px] tabular-nums min-w-[32px] text-center">{qty}</span>
                <button
                  type="button"
                  onClick={() => setQty(Math.min(product.stock_count === -1 ? 99 : product.stock_count, qty + 1))}
                  disabled={product.stock_count !== -1 && qty >= product.stock_count}
                  className="px-3 py-2.5 text-[var(--color-muted)] hover:text-[var(--color-navy-700)] disabled:opacity-30"
                  aria-label={locale === "en" ? "Increase" : "Flere"}
                >
                  <Plus size={14} strokeWidth={2.5} />
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={handleAdd}
              disabled={outOfStock}
              className={`btn !py-2.5 inline-flex items-center gap-2 transition-all ${
                justAdded
                  ? "bg-[#166534] hover:bg-[#166534] text-white"
                  : outOfStock
                  ? "bg-[var(--color-surface-200)] text-[var(--color-muted)] cursor-not-allowed"
                  : "btn-primary"
              }`}
            >
              {justAdded ? (
                <>
                  <Check size={15} strokeWidth={2.5} />
                  {locale === "en" ? "Added" : "Tilføjet"}
                </>
              ) : outOfStock ? (
                <>
                  {locale === "en" ? "Sold out" : "Udsolgt"}
                </>
              ) : (
                <>
                  <ShoppingCart size={15} strokeWidth={2} />
                  {locale === "en" ? "Add to cart" : "Læg i kurv"}
                </>
              )}
            </button>

            {product.sku && (
              <span className="text-[11.5px] text-[var(--color-muted)] font-mono ml-auto">
                SKU: {product.sku}
              </span>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
